import React from 'react';
import { ApproachTreeNode } from 'types/approachExplorer';
import ApproachTreeNodeAtom from '../../atoms/ApproachTreeNode/ApproachTreeNodeAtom';
import './ApproachTreeMolecule.scss';

export interface ApproachTreeMoleculeProps {
  /** Root nodes of the tree to render. */
  nodes: ApproachTreeNode[];
  /** Set of node IDs that are currently expanded. */
  expandedNodeIds: Set<string>;
  /** ID of the currently selected node, if any. */
  selectedNodeId?: string | null;
  /** Called when a node's expand/collapse chevron is clicked. */
  onToggle: (nodeId: string) => void;
  /** Called when a node row is clicked for selection. */
  onSelect: (nodeId: string) => void;
  /** Additional CSS class name. */
  className?: string;
}

interface TreeNodeListProps {
  nodes: ApproachTreeNode[];
  level: number;
  expandedNodeIds: Set<string>;
  selectedNodeId?: string | null;
  onToggle: (nodeId: string) => void;
  onSelect: (nodeId: string) => void;
}

const TreeNodeList: React.FC<TreeNodeListProps> = ({
  nodes,
  level,
  expandedNodeIds,
  selectedNodeId,
  onToggle,
  onSelect,
}) => {
  return (
    <>
      {nodes.map((node) => {
        const isExpanded = expandedNodeIds.has(node.id);

        return (
          <React.Fragment key={node.id}>
            <ApproachTreeNodeAtom
              id={node.id}
              label={node.label}
              icon={node.icon}
              level={level}
              expandable={node.expandable || (node.children != null && node.children.length > 0)}
              expanded={isExpanded}
              selected={selectedNodeId === node.id}
              loading={node.loading}
              error={node.error}
              onToggle={onToggle}
              onSelect={onSelect}
              data-testid={`tree-node-${node.id}`}
            />
            {isExpanded && node.children && node.children.length > 0 && (
              <TreeNodeList
                nodes={node.children}
                level={level + 1}
                expandedNodeIds={expandedNodeIds}
                selectedNodeId={selectedNodeId}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

const ApproachTreeMolecule: React.FC<ApproachTreeMoleculeProps> = ({
  nodes,
  expandedNodeIds,
  selectedNodeId,
  onToggle,
  onSelect,
  className,
}) => {
  const classes = ['approach-tree-molecule', className].filter(Boolean).join(' ');

  return (
    <div className={classes} role="tree" data-testid="approach-tree">
      <TreeNodeList
        nodes={nodes}
        level={0}
        expandedNodeIds={expandedNodeIds}
        selectedNodeId={selectedNodeId}
        onToggle={onToggle}
        onSelect={onSelect}
      />
    </div>
  );
};

ApproachTreeMolecule.displayName = 'ApproachTreeMolecule';

export default ApproachTreeMolecule;
